import { AuthenticatedHttp } from '@/http/axios';
import { AppError } from '@/utils/api';
import { CACHE_TTL } from '../constants';

const cache = new Map();

function getCached(key, fetcher) {
  const now = Date.now();
  const cached = cache.get(key);

  if (cached) {
    if (cached.data && now - cached.ts < CACHE_TTL) return Promise.resolve(cached.data);
    if (cached.promise) return cached.promise;
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, ts: Date.now() });
      return data;
    })
    .catch((e) => {
      cache.delete(key);
      throw e;
    });

  cache.set(key, { promise, ts: now });

  return promise;
}

const buildCacheKey = (key, params = {}) => `${key}:${JSON.stringify(params)}`;

const getCollaboratorRows = (data) => {
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

const hasActiveCollaboratorInvite = (data, ownerId) =>
  getCollaboratorRows(data).some(
    (collaborator) =>
      String(collaborator?.owner?.id) === String(ownerId) &&
      collaborator?.status === 'active'
  );

export const clearCollaboratorsCache = () => {
  cache.clear();
};

export const fetchColaboratorsService = async (params = {}) =>
  getCached(buildCacheKey('collaborators_owner', params), () =>
    AuthenticatedHttp.get('/collaborators/', { params: { type: 'owner', ...params } }).then(({ data }) => data)
  );

export const fetchInvitationsService = async (params = {}) =>
  getCached(buildCacheKey('collaborators_collaborator', params), () =>
    AuthenticatedHttp.get('/collaborators/', { params: { type: 'collaborator', ...params } }).then(({ data }) => data)
  );

export const validateImpersonatedAccountAccess = async ({ ownerId }) => {
  if (!ownerId) return false;

  const { data } = await AuthenticatedHttp.get('/collaborators/', {
    params: { type: 'collaborator', page_size: 1000 },
    skipImpersonation: true,
  });

  return hasActiveCollaboratorInvite(data, ownerId);
};

export const sendColaboratorInvite = async ({ email }) =>
  AuthenticatedHttp.post('/collaborators/', { email }).then(({ data }) => {
    clearCollaboratorsCache();
    return data;
  });

export const cancelColaboratorInvite = async ({ collaboratorId }) =>
  AuthenticatedHttp.post(`/collaborators/${collaboratorId}/cancel/`).then(({ data }) => {
    clearCollaboratorsCache();
    return data;
  });

export const resendColaboratorInvite = async ({ collaboratorEmail }) =>
  AuthenticatedHttp.post('/collaborators/resend-invite/', { email: collaboratorEmail }).then(({ data }) => {
    clearCollaboratorsCache();
    return data;
  });

export const acceptColaboratorInvite = async ({ collaboratorId }) =>
  AuthenticatedHttp.post(`/collaborators/${collaboratorId}/accept/`).then(({ data }) => {
    clearCollaboratorsCache();
    return data;
  });

export const rejectColaboratorInvite = async ({ collaboratorId }) =>
  AuthenticatedHttp.post(`/collaborators/${collaboratorId}/reject/`).then(({ data }) => {
    clearCollaboratorsCache();
    return data;
  });

export const deleteCollaborator = async ({ id }) =>
  AuthenticatedHttp.delete(`/collaborators/${id}/`).catch((error) => {
    clearCollaboratorsCache();
    throw new AppError(error);
  });
